import {
  Activity,
  BarChart3,
  Bike,
  Box,
  Building,
  Building2,
  CalendarCheck,
  Code,
  CreditCard,
  FileText,
  Gavel,
  LayoutDashboard,
  Lock,
  Map as MapIcon,
  MapPin,
  MessageSquare,
  PieChart,
  Receipt,
  Scale,
  Send,
  Settings,
  Share2,
  Shield,
  Smartphone,
  Ticket,
  TicketCheck,
  ToggleLeft,
  Trophy,
  UserCircle,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { LANTERNE_WHITELABEL_ID } from "@/constants/const";
import lanterneLogo from "@/images/lanterne-logo-dark.png";
import type {
  SidebarConfig,
  SidebarItem as SidebarConfigItem,
  SidebarPreferences,
} from "@/v2/components/sidebar";
import { useEffectiveWhitelabelId } from "@/v2/hooks/useEffectiveWhitelabelId";
import { shouldOfferVehicleModels } from "@/v2/hooks/useVehicleModelsEnabled";
import type { SidebarConfigParams } from "../types";

interface UseSidebarConfigParams {
  adminDetails: SidebarConfigParams["adminDetails"];
  strings: SidebarConfigParams["strings"];
  effectiveIsSuperAdmin: boolean;
  effectiveIsCityAdmin: boolean;
  routes: SidebarConfigParams["routes"];
  itemHandlers: Record<string, () => void>;
  preferences: SidebarPreferences;
  normalizedPath: string;
  featureFlags: SidebarConfigParams["featureFlags"];
}

interface UseSidebarConfigResult {
  visibleSidebarConfig: SidebarConfig;
  orderedSidebarConfig: SidebarConfig;
  isSidebarItemSelected: (item: SidebarConfigItem) => boolean;
}

/**
 * Applies custom item ordering to sidebar groups based on user preferences.
 * Items not in the order array are appended at the end.
 */
const applySidebarItemOrder = (
  config: SidebarConfig,
  itemOrder: SidebarPreferences["itemOrder"]
): SidebarConfig =>
  config.map((group) => {
    const order = itemOrder[group.id] || [];
    if (order.length === 0) return group;

    const remainingItems = new Map(group.items.map((item) => [item.id, item]));
    const orderedItems: SidebarConfigItem[] = [];

    order.forEach((itemId) => {
      const item = remainingItems.get(itemId);
      if (item) {
        orderedItems.push(item);
        remainingItems.delete(itemId);
      }
    });

    remainingItems.forEach((item) => {
      orderedItems.push(item);
    });

    return { ...group, items: orderedItems };
  });

/**
 * Filters out hidden items from sidebar configuration based on user preferences.
 * Groups with no visible items are also removed.
 */
const applySidebarVisibility = (
  config: SidebarConfig,
  hiddenItems: SidebarPreferences["hiddenItems"]
): SidebarConfig =>
  config
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !hiddenItems.includes(item.id)),
    }))
    .filter((group) => group.items.length > 0);

/**
 * Generates the complete sidebar configuration based on admin permissions and features.
 */
function getSidebarConfig({
  adminDetails,
  strings,
  effectiveIsSuperAdmin,
  effectiveIsCityAdmin,
  routes,
  itemHandlers,
  featureFlags,
  whitelabelId,
}: SidebarConfigParams & { whitelabelId: number | null }): SidebarConfig {
  const config: SidebarConfig = [];

  const mainNavGroup: SidebarConfig[number] = {
    id: "main-navigation",
    name: strings("sidebar.fleetOperations"),
    icon: Wrench,
    items: [
      {
        id: "overview",
        name: strings("descriptions.overview"),
        icon: LayoutDashboard,
        route: routes.DASHBOARD_SCREEN,
        groupId: "main-navigation",
        dataTour: "sidebar-overview",
        onClick: itemHandlers.overview,
      },
      {
        id: "map",
        name: strings("descriptions.map"),
        icon: MapIcon,
        route: routes.DASHBOARD_MAP,
        groupId: "main-navigation",
        dataTour: "sidebar-map",
        onClick: itemHandlers.map,
      },
      {
        id: "bikes",
        name: strings("descriptions.bikes"),
        icon: Bike,
        route: routes.DASHBOARD_BIKES,
        groupId: "main-navigation",
        dataTour: "sidebar-bikes",
        onClick: itemHandlers.bikes,
      },
      // Sits with Bikes rather than under Administration: it is a description of
      // the fleet an operator manages daily, not a configuration screen they
      // visit once. Hidden from city admins — the backend refuses them, so the
      // entry would only lead to a 403 — and behind the flag wherever the flag
      // can actually be read. See `shouldOfferVehicleModels` for the case where
      // it cannot be.
      ...(shouldOfferVehicleModels(featureFlags, adminDetails, whitelabelId)
        ? [
            {
              id: "vehicle-models",
              name: strings("sidebar.vehicleModels"),
              icon: Bike,
              route: routes.DASHBOARD_VEHICLE_MODELS,
              groupId: "main-navigation",
              onClick: itemHandlers["vehicle-models"],
            },
          ]
        : []),
      {
        id: "locks",
        name: strings("descriptions.locks"),
        icon: Lock,
        route: routes.DASHBOARD_LOCKS,
        groupId: "main-navigation",
        onClick: itemHandlers.locks,
      },
      {
        id: "zones",
        name: strings("descriptions.stations"),
        icon: MapPin,
        route: routes.DASHBOARD_STATIONS,
        groupId: "main-navigation",
        onClick: itemHandlers.zones,
      },
      {
        id: "docks",
        name: strings("descriptions.docks"),
        icon: Box,
        route: routes.DASHBOARD_DOCKS,
        groupId: "main-navigation",
        onClick: itemHandlers.docks,
      },
      {
        id: "rentals",
        name: strings("descriptions.rentals"),
        icon: Receipt,
        route: routes.DASHBOARD_RENTALS,
        groupId: "main-navigation",
        dataTour: "sidebar-rentals",
        onClick: itemHandlers.rentals,
      },
      // Sits next to Rentals because a fine is always about one: support
      // arrives here from a complaint about a ride, not from a menu.
      {
        id: "fines",
        name: strings("fines.fines"),
        icon: Gavel,
        route: routes.DASHBOARD_FINES,
        groupId: "main-navigation",
        onClick: itemHandlers.fines,
      },
    ],
  };

  if (
    effectiveIsSuperAdmin ||
    ((effectiveIsCityAdmin || adminDetails?.cityId != null) &&
      adminDetails?.newBackendEnabled)
  ) {
    mainNavGroup.items.push({
      id: "reservations",
      name: strings("sidebar.reservations"),
      icon: CalendarCheck,
      route: routes.DASHBOARD_RESERVATIONS,
      groupId: "main-navigation",
      onClick: itemHandlers.reservations,
    });
  }

  if (effectiveIsCityAdmin) {
    mainNavGroup.items.push({
      id: "rental-rules",
      name: strings("descriptions.rentalRules"),
      icon: Scale,
      route: routes.DASHBOARD_RENTAL_RULES,
      groupId: "main-navigation",
      onClick: itemHandlers["rental-rules"],
    });
  }

  if (adminDetails?.whiteLabelId === LANTERNE_WHITELABEL_ID) {
    mainNavGroup.items.push({
      id: "lanterne",
      name: strings("sidebar.lanterne"),
      description: strings("sidebar.lanterneDescription"),
      icon: lanterneLogo,
      isExternal: true,
      externalUrl:
        "https://dashboard.lanterne.ai/fleets/dashboard?fleet=lisa-demo",
      groupId: "main-navigation",
    });
  }

  config.push(mainNavGroup);

  const transactionsUsersGroup: SidebarConfig[number] = {
    id: "transactions-users",
    name: strings("sidebar.customers"),
    icon: UserCircle,
    items: [
      {
        id: "transactions",
        name: strings("descriptions.transactions"),
        icon: CreditCard,
        route: routes.DASHBOARD_TRANSACTIONS,
        groupId: "transactions-users",
        onClick: itemHandlers.transactions,
      },
      {
        id: "users",
        name: strings("descriptions.users"),
        icon: Users,
        route: routes.DASHBOARD_USERS,
        groupId: "transactions-users",
        dataTour: "sidebar-users",
        onClick: itemHandlers.users,
      },
    ],
  };

  config.push(transactionsUsersGroup);

  if (effectiveIsCityAdmin) {
    transactionsUsersGroup.items.push({
      id: "customer-restrictions",
      name: strings("sidebar.customerRestrictions"),
      icon: Shield,
      route: routes.CUSTOMER_RESTRICTIONS_ROUTE,
      groupId: "transactions-users",
      onClick: itemHandlers["customer-restrictions"],
    });
  }

  const ridePassesEnabled = Boolean(
    effectiveIsSuperAdmin || adminDetails?.newBackendEnabled
  );

  // Pricing is one entry, not one per storage shape. Ride pricing and ride
  // passes are tabs inside it, so an operator asking "what does a ride cost
  // here" has a single place to go.
  //
  // Ride pricing is open to every admin, including one scoped to a single
  // city: it used to be reachable only through Cities, which city admins
  // cannot see, so their own city's prices were invisible to them. Each admin
  // still only sees the city they are scoped to. Ride passes keeps the
  // new-backend gate it carried in Promotions, so the group is built from
  // whichever items this admin can actually reach.
  const pricingItems: SidebarConfigItem[] = [
    {
      id: "ride-pricing",
      name: strings("pricingHub.ridePricing"),
      icon: CreditCard,
      route: `${routes.PRICING_HUB_ROUTE}?tab=ride-pricing`,
      groupId: "pricing",
      onClick: itemHandlers["ride-pricing"],
    },
  ];

  if (ridePassesEnabled) {
    pricingItems.push({
      id: "ride-passes",
      name: strings("pricingHub.ridePasses"),
      icon: TicketCheck,
      route: `${routes.PRICING_HUB_ROUTE}?tab=ride-passes`,
      groupId: "pricing",
      onClick: itemHandlers["ride-passes"],
    });
  }

  if (pricingItems.length > 0) {
    config.push({
      id: "pricing",
      name: strings("pricingHub.title"),
      icon: CreditCard,
      items: pricingItems,
    });
  }

  if (ridePassesEnabled) {
    const promotionsGroup: SidebarConfig[number] = {
      id: "promotions",
      name: strings("sidebar.promotions"),
      icon: Trophy,
      items: [
        {
          id: "coupons",
          name: strings("descriptions.coupons"),
          icon: Ticket,
          route: routes.COUPONS_ROUTE,
          groupId: "promotions",
          onClick: itemHandlers.coupons,
        },
        {
          id: "referral",
          name: strings("descriptions.referral"),
          icon: Share2,
          route: routes.REFERRAL_ROUTE,
          groupId: "promotions",
          onClick: itemHandlers.referral,
        },
        {
          id: "rewards",
          name: strings("sidebar.rewards"),
          icon: Trophy,
          route: routes.PROMOTIONS_REWARDS_ROUTE,
          groupId: "promotions",
          onClick: itemHandlers.rewards,
        },
        {
          id: "topup-bonuses",
          name: strings("descriptions.topupBonuses"),
          icon: Wallet,
          route: routes.PROMOTIONS_TOPUP_BONUSES_ROUTE,
          groupId: "promotions",
          onClick: itemHandlers["topup-bonuses"],
        },
      ],
    };
    config.push(promotionsGroup);
  } else {
    transactionsUsersGroup.items.push(
      {
        id: "coupons",
        name: strings("descriptions.coupons"),
        icon: Ticket,
        route: routes.COUPONS_ROUTE,
        groupId: "transactions-users",
        onClick: itemHandlers.coupons,
      },
      {
        id: "referral",
        name: strings("descriptions.referral"),
        icon: Share2,
        route: routes.REFERRAL_ROUTE,
        groupId: "transactions-users",
        onClick: itemHandlers.referral,
      }
    );
  }

  const administrationGroup: SidebarConfig[number] = {
    id: "administration",
    name: strings("descriptions.administration"),
    icon: Shield,
    dataTour: "sidebar-admin",
    items: [],
  };

  if (effectiveIsCityAdmin) {
    administrationGroup.items.push({
      id: "cities",
      name: strings("descriptions.cities"),
      icon: Building,
      route: routes.DASHBOARD_CITIES,
      groupId: "administration",
      onClick: itemHandlers.cities,
    });
  }

  administrationGroup.items.push({
    id: "app-configuration",
    name: strings("sidebar.appConfiguration"),
    icon: Settings,
    route: routes.APP_CONFIGURATION_ROUTE,
    groupId: "administration",
    onClick: itemHandlers["app-configuration"],
  });

  if (effectiveIsSuperAdmin) {
    administrationGroup.items.push(
      {
        id: "organizations",
        name: strings("organizations.title"),
        icon: Building2,
        route: routes.DASHBOARD_ORGANIZATIONS,
        groupId: "administration",
        onClick: itemHandlers.organizations,
      },
      {
        id: "user-management",
        name: strings("sidebar.userManagement"),
        icon: Users,
        route: routes.USER_MANAGEMENT_ROUTE,
        groupId: "administration",
        onClick: itemHandlers["user-management"],
      },
      {
        id: "service-accounts",
        name: strings("sidebar.serviceAccounts"),
        icon: Wallet,
        route: routes.SERVICE_ACCOUNTS_ROUTE,
        groupId: "administration",
        onClick: itemHandlers["service-accounts"],
      }
    );
  }

  if (
    effectiveIsSuperAdmin ||
    (effectiveIsCityAdmin && adminDetails?.newBackendEnabled)
  ) {
    administrationGroup.items.push({
      id: "notifications",
      name: strings("descriptions.notifications"),
      icon: Send,
      route: routes.NOTIFICATIONS_ROUTE,
      groupId: "administration",
      onClick: itemHandlers.notifications,
    });
  }

  if (featureFlags.supersetAnalytics) {
    administrationGroup.items.push({
      id: "analytics-v2",
      name: strings("sidebar.analyticsV2"),
      icon: PieChart,
      route: routes.DASHBOARD_ANALYTICS_V2,
      groupId: "administration",
      dataTour: "sidebar-analytics",
      onClick: itemHandlers["analytics-v2"],
    });
  }

  if (effectiveIsCityAdmin && featureFlags.partnerAccess) {
    administrationGroup.items.push({
      id: "partners",
      name: strings("sidebar.partners"),
      icon: Share2,
      route: routes.DASHBOARD_PARTNERS,
      groupId: "administration",
      onClick: itemHandlers.partners,
    });
  }

  if (
    effectiveIsCityAdmin &&
    featureFlags.legacyAnalytics &&
    (effectiveIsSuperAdmin || adminDetails?.newBackendEnabled)
  ) {
    administrationGroup.items.push({
      id: "analytics",
      name: strings("sidebar.analytics"),
      icon: BarChart3,
      route: routes.DASHBOARD_ANALYTICS,
      groupId: "administration",
      onClick: itemHandlers.analytics,
    });
  }

  if (effectiveIsCityAdmin && featureFlags.aiAnalytics) {
    administrationGroup.items.push({
      id: "chat-analytics",
      name: strings("sidebar.chatAnalytics"),
      icon: MessageSquare,
      route: routes.DASHBOARD_CHAT_ANALYTICS,
      groupId: "administration",
      onClick: itemHandlers["chat-analytics"],
    });
  }

  if (administrationGroup.items.length > 0) {
    config.push(administrationGroup);
  }

  if (effectiveIsSuperAdmin) {
    const developerGroup: SidebarConfig[number] = {
      id: "developer",
      name: strings("sidebar.developer"),
      icon: Code,
      items: [
        {
          id: "app-versions",
          name: strings("sidebar.appVersions"),
          icon: Smartphone,
          route: routes.APP_VERSIONS_ROUTE,
          groupId: "developer",
          onClick: itemHandlers["app-versions"],
        },
        {
          id: "service-health",
          name: strings("sidebar.serviceHealth"),
          icon: Activity,
          route: routes.SERVICE_HEALTH_ROUTE,
          groupId: "developer",
          onClick: itemHandlers["service-health"],
        },
        {
          id: "api-docs",
          name: strings("sidebar.apiDocs"),
          icon: FileText,
          route: routes.API_DOCS_ROUTE,
          groupId: "developer",
          onClick: itemHandlers["api-docs"],
        },
        {
          id: "feature-flags",
          name: strings("sidebar.featureFlags"),
          icon: ToggleLeft,
          route: routes.FEATURE_FLAGS_ROUTE,
          groupId: "developer",
          onClick: itemHandlers["feature-flags"],
        },
      ],
    };
    config.push(developerGroup);
  }

  return config;
}

/**
 * Hook for managing sidebar configuration, ordering, visibility, and selection state.
 * Extracts sidebar logic from the dashboard screen for better separation of concerns.
 */
export function useSidebarConfig({
  adminDetails,
  strings,
  effectiveIsSuperAdmin,
  effectiveIsCityAdmin,
  routes,
  itemHandlers,
  preferences,
  normalizedPath,
  featureFlags,
}: UseSidebarConfigParams): UseSidebarConfigResult {
  const [pricingSearchParams] = useSearchParams();
  const whitelabelId = useEffectiveWhitelabelId();

  // Generate base sidebar configuration based on permissions
  const baseSidebarConfig = useMemo(
    () =>
      getSidebarConfig({
        adminDetails,
        strings,
        effectiveIsSuperAdmin,
        effectiveIsCityAdmin,
        routes,
        itemHandlers,
        featureFlags,
        whitelabelId,
      }),
    [
      adminDetails,
      strings,
      effectiveIsSuperAdmin,
      effectiveIsCityAdmin,
      routes,
      itemHandlers,
      featureFlags,
      whitelabelId,
    ]
  );

  // Apply custom item ordering from preferences
  const orderedSidebarConfig = useMemo(
    () => applySidebarItemOrder(baseSidebarConfig, preferences.itemOrder),
    [baseSidebarConfig, preferences.itemOrder]
  );

  // Apply visibility filtering from preferences
  const visibleSidebarConfig = useMemo(
    () => applySidebarVisibility(orderedSidebarConfig, preferences.hiddenItems),
    [orderedSidebarConfig, preferences.hiddenItems]
  );

  // Determine if a sidebar item is currently selected based on the current path
  // Ride pricing and ride passes are two tabs of one route, so the path alone
  // cannot say which sidebar item is the current one.
  const activePricingTab = pricingSearchParams.get("tab");

  const isSidebarItemSelected = useCallback(
    (item: SidebarConfigItem) => {
      switch (item.id) {
        case "bikes":
          return (
            normalizedPath === routes.DASHBOARD_BIKES ||
            normalizedPath.startsWith("/dashboard/bike/")
          );
        case "vehicle-models":
          // The detail route nests under the list route, so a plain equality
          // check would drop the highlight the moment a model is opened.
          return normalizedPath.startsWith(routes.DASHBOARD_VEHICLE_MODELS);
        case "users":
          return (
            normalizedPath === routes.DASHBOARD_USERS ||
            normalizedPath.startsWith("/dashboard/ledger/") ||
            normalizedPath.startsWith("/dashboard/customer/")
          );
        case "coupons":
          return (
            normalizedPath === routes.COUPONS_ROUTE ||
            normalizedPath.startsWith("/dashboard/coupon-user/")
          );
        case "rewards":
          return normalizedPath.startsWith(routes.PROMOTIONS_REWARDS_ROUTE);
        case "topup-bonuses":
          return normalizedPath === routes.PROMOTIONS_TOPUP_BONUSES_ROUTE;
        case "ride-pricing":
          return (
            (normalizedPath === routes.PRICING_HUB_ROUTE &&
              activePricingTab !== "ride-passes") ||
            normalizedPath.startsWith("/dashboard/pricing-plan-editor/") ||
            normalizedPath.startsWith("/dashboard/pricing-plan-domains/")
          );
        case "ride-passes":
          return (
            (normalizedPath === routes.PRICING_HUB_ROUTE &&
              activePricingTab === "ride-passes") ||
            normalizedPath === routes.PROMOTIONS_RIDE_PASSES_ROUTE
          );
        case "cities":
          return (
            normalizedPath === routes.DASHBOARD_CITIES ||
            normalizedPath.startsWith("/dashboard/cities/")
          );
        default:
          return item.route ? normalizedPath === item.route : false;
      }
    },
    [normalizedPath, routes, activePricingTab]
  );

  return {
    visibleSidebarConfig,
    orderedSidebarConfig,
    isSidebarItemSelected,
  };
}
